import { useEffect, useState } from "react";
import { useAba } from "@/lib/useAba";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PageHeader } from "@/components/common";
import { PurchaseOrders } from "@/components/stock/PurchaseOrders";
import { StockPosition } from "@/components/stock/StockPosition";
import { Movements } from "@/components/stock/Movements";
import { Inventory } from "@/components/stock/Inventory";
import { Registry } from "@/components/stock/Registry";
import { StockReports } from "@/components/stock/StockReports";
import { NfeImportButton } from "@/components/stock/NfeImport";
import { supplierFromCnpj, addressFromCep } from "@/lib/lookups";
import { useAuth } from "@/context/AuthContext";
import { api, brl, canDo, fmtQty, maskPhone, UNIT_LABELS } from "@/lib/api";

const ING_FIELDS = [
  { key: "name", label: "Nome do insumo", wide: true },
  { key: "unit", label: "Unidade", options: Object.entries(UNIT_LABELS) },
  { key: "min_stock", label: "Estoque mínimo", type: "number" },
  { key: "cost", label: "Custo unitário (R$)", type: "number" },
];
const ING_COLS = [
  { label: "Insumo", render: (x) => <span className="font-medium">{x.name}</span> },
  { label: "Saldo", render: (x) => <span className={`font-mono ${x.stock <= x.min_stock ? "text-red-500" : ""}`}>{fmtQty(x.stock)} {x.unit}</span> },
  { label: "Mínimo", render: (x) => fmtQty(x.min_stock) },
  { label: "Custo médio", render: (x) => brl(x.cost) },
];
const SUP_FIELDS = [
  { key: "document", label: "CPF/CNPJ", wide: true, hint: "CNPJ: digite e saia do campo (ou Enter) para buscar os dados na Receita · CPF: preencha manualmente", onBlur: supplierFromCnpj },
  { key: "name", label: "Nome / razão social", wide: true },
  { key: "trade_name", label: "Nome fantasia" },
  { key: "contact", label: "Contato" },
  { key: "phone", label: "Telefone" },
  { key: "email", label: "E-mail" },
  { key: "cep", label: "CEP", hint: "Preenche o endereço", onBlur: addressFromCep },
  { key: "street", label: "Endereço" },
  { key: "number", label: "Número" },
  { key: "complement", label: "Complemento" },
  { key: "district", label: "Bairro" },
  { key: "city", label: "Cidade" },
  { key: "state", label: "UF" },
  { key: "notes", label: "Observações", wide: true },
];
const SUP_COLS = [
  { label: "Fornecedor", render: (x) => <span className="font-medium">{x.name}</span> },
  { label: "CPF/CNPJ", render: (x) => <span className="font-mono text-xs">{x.document || "—"}</span> },
  { label: "Cidade", render: (x) => (x.city ? `${x.city}${x.state ? `/${x.state}` : ""}` : "—") },
  { label: "Contato", render: (x) => x.contact || "—" },
  { label: "Telefone", render: (x) => (x.phone ? maskPhone(x.phone) : "—") },
  { label: "E-mail", render: (x) => x.email || "—" },
];

export default function Stock() {
  const aba = useAba("posicao");
  const { session } = useAuth();
  const u = session.user;
  const canChange = canDo(u, "change_stock");
  const canDelete = canDo(u, "delete_records");
  const [suppliers, setSuppliers] = useState([]);
  const [rev, setRev] = useState(0);
  useEffect(() => { api.get("/suppliers").then((r) => setSuppliers(r.data)); }, [rev]);
  const ingFields = [...ING_FIELDS, { key: "supplier_id", label: "Fornecedor", wide: true, options: [["", "Sem fornecedor"], ...suppliers.map((s) => [s.id, s.name])] }];
  const mode = session.tenant.settings.stock_negative === "block" ? "Venda sem estoque bloqueada" : "Venda sem estoque permitida com alerta";
  return (
    <>
      <PageHeader eyebrow="Controle de estoque" title="Estoque" subtitle={`Baixa automática após a venda · ${mode}`}
        actions={canChange && <NfeImportButton onDone={() => setRev((r) => r + 1)} />} />
      <Tabs key={aba} defaultValue={aba}>
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="posicao" data-testid="stock-tab-posicao">Posição</TabsTrigger>
          <TabsTrigger value="movimentacoes" data-testid="stock-tab-movimentacoes">Histórico</TabsTrigger>
          <TabsTrigger value="inventario" data-testid="stock-tab-inventario">Inventário</TabsTrigger>
          <TabsTrigger value="insumos" data-testid="stock-tab-insumos">Insumos</TabsTrigger>
          <TabsTrigger value="fornecedores" data-testid="stock-tab-fornecedores">Fornecedores</TabsTrigger>
          {canChange && <TabsTrigger value="compras" data-testid="stock-tab-compras">Pedido de compra</TabsTrigger>}
          {canDo(u, "view_reports") && <TabsTrigger value="relatorios" data-testid="stock-tab-relatorios">Relatórios</TabsTrigger>}
        </TabsList>
        <TabsContent value="posicao" className="mt-4"><StockPosition key={rev} canChange={canChange} /></TabsContent>
        <TabsContent value="movimentacoes" className="mt-4"><Movements key={rev} /></TabsContent>
        <TabsContent value="inventario" className="mt-4"><Inventory canChange={canChange} /></TabsContent>
        <TabsContent value="insumos" className="mt-4"><Registry key={`ing-${rev}`} endpoint="/ingredients" fields={ingFields} columns={ING_COLS} empty={{ name: "", unit: "kg", min_stock: 0, cost: 0, supplier_id: "" }} title="Novo insumo" canEdit={canDo(u, "edit_products")} canDelete={canDelete} testId="ingredients" /></TabsContent>
        {canChange && <TabsContent value="compras" className="mt-4"><PurchaseOrders store={session.tenant.nome_fantasia} /></TabsContent>}
        <TabsContent value="fornecedores" className="mt-4"><Registry key={`sup-${rev}`} endpoint="/suppliers" fields={SUP_FIELDS} columns={SUP_COLS} empty={{ name: "" }} title="Novo fornecedor" canEdit={canChange} canDelete={canDelete} testId="suppliers" wide /></TabsContent>
        {canDo(u, "view_reports") && <TabsContent value="relatorios" className="mt-4"><StockReports /></TabsContent>}
      </Tabs>
    </>
  );
}
